<template>
  <div class="cs-root sc-page-root">
    <FiPageRail title="Flagged Drivers" class="cs-rail">
      <template #actions>
        <coaching-portal-help />
      </template>
    </FiPageRail>

    <div v-if="actingTenantDiffers" class="cs-tenant-note">
      <i class="fa fa-building-o" aria-hidden="true"></i>
      Viewing <b>{{ actingTenantLabel }}</b> — not your selected fleet
    </div>

    <div v-if="flaggedLoading && !flaggedDrivers.length && !flaggedError" class="text-center" style="margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="flaggedError" class="text-center text-danger" style="margin: 50px;">
      <p>{{ flaggedError }}</p>
      <b-button size="sm" variant="outline-secondary" @click="loadFlaggedDrivers()">Retry</b-button>
    </div>

    <div v-else :class="{ 'cs-stale': flaggedLoading, 'cs-filters-collapsed': !filtersExpanded }">
      <!-- search/filter bar in the Coaching Tickets vocabulary. the Period
           months re-query the scorecard window; the rest filters the rows. -->
      <!-- mobile-only filters toggle (the ERM Event Log pattern, ops_desk.vue):
           collapses every filter control behind one tap so rows reach above
           the fold on a phone; the search pill stays visible. the badge
           counts active filter dimensions (search excluded — it has its own
           always-on field). display:none above 600px, so desktop is
           unaffected. -->
      <button type="button" class="cs-filter-toggle cs-mobile-only"
              :aria-expanded="filtersExpanded ? 'true' : 'false'"
              @click="filtersExpanded = !filtersExpanded">
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeFilterCount" class="cs-filter-badge">{{ activeFilterCount }}</span>
        <i class="fa" :class="filtersExpanded ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>
      <div class="cs-filter-row" role="search" aria-label="Filter flagged drivers">
        <!-- one-tap period presets: the scorecard window is month-granular,
             so Nd selects the months covering the last N days. tapping the
             active chip returns to the default window. -->
        <span class="cs-filter-field" role="group" aria-label="Period covering the last">
          <button v-for="p in periodPresets" :key="'pp-' + p" type="button"
                  :class="['cs-preset-chip', { 'cs-preset-chip--active': periodPresetActive(p) }]"
                  :aria-pressed="periodPresetActive(p) ? 'true' : 'false'"
                  @click="setPeriodPreset(p)">{{ p }}d</button>
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">Period from</span>
          <b-form-input v-model="scorecardFrom" type="month" size="sm"
                        :class="['cs-filter-month', { 'cs-month-ghost': !scorecardFrom }]"
                        @change="onPeriodChange" />
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">To</span>
          <b-form-input v-model="scorecardTo" type="month" size="sm"
                        :class="['cs-filter-month', { 'cs-month-ghost': !scorecardTo }]"
                        @change="onPeriodChange" />
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">Metric</span>
          <b-form-select v-model="flaggedMetric" :options="metricFilterOptions" size="sm" class="cs-filter-select" />
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">Severity</span>
          <b-form-select v-model="flaggedSeverity" :options="severityFilterOptions" size="sm" class="cs-filter-select cs-filter-select--narrow" />
        </span>
        <span class="cs-filter-field">
          <span class="cs-filter-lbl">Last coached</span>
          <b-form-select v-model="flaggedCoached" :options="coachedFilterOptions" size="sm" class="cs-filter-select cs-filter-select--narrow" />
        </span>
        <span class="cs-search">
          <i class="fa fa-search cs-search-icon" aria-hidden="true"></i>
          <input v-model="flaggedSearch" type="text" class="cs-search-input"
                 placeholder="Search driver or metric"
                 aria-label="Search flagged drivers by name or metric" />
          <button v-if="flaggedSearch" type="button" class="cs-search-clear" aria-label="Clear search"
                  @click="flaggedSearch = ''"><i class="fa fa-times" aria-hidden="true"></i></button>
        </span>
      </div>
      <div class="cs-results-row">
        <span class="cs-results-count">
          {{ flaggedRowsFiltered.length === flaggedRows.length
            ? `${flaggedRows.length} driver${flaggedRows.length === 1 ? '' : 's'} flagged`
            : `${flaggedRowsFiltered.length} of ${flaggedRows.length} flagged drivers` }}
          · {{ scorecardPeriodLabel }}
        </span>
        <button v-if="driverFilter" type="button" class="cs-clear-filters" @click="clearDriverFilter">
          <i class="fa fa-times" aria-hidden="true"></i>&nbsp;Showing only {{ driverFilterLabel }}
        </button>
        <button v-if="otherFiltersDirty" type="button" class="cs-clear-filters" @click="clearFilters">
          <i class="fa fa-times" aria-hidden="true"></i>&nbsp;Reset filters
        </button>
      </div>

      <b-card class="cs-panel mt-2" no-body>
        <div class="cs-panel-head">
          Over a coaching threshold <span class="cs-hint">from the Scorecard · {{ scorecardPeriodLabel }}</span>
        </div>
        <div class="cs-panel-body">
          <div v-if="!flaggedRowsFiltered.length" class="cs-small text-muted">
            <span v-if="!flaggedDrivers.length">No drivers are over a coaching threshold this period.</span>
            <span v-else-if="driverFilter && !driverFilterHasFlags">
              {{ driverFilterLabel }} is not over a coaching threshold in this period ({{ scorecardPeriodLabel }}).
              This page keeps its own date range, so a driver flagged on their scorecard can be clear here.
              <b-button size="sm" variant="outline-secondary" class="ml-2" @click="clearDriverFilter">Show all flagged drivers</b-button>
            </span>
            <span v-else>
              No flagged drivers match these filters.
              <b-button size="sm" variant="outline-secondary" class="ml-2" @click="clearFilters">Reset filters</b-button>
            </span>
          </div>
          <div v-else class="cs-table-inline">
            <b-table :items="flaggedRowsPage" :fields="flaggedFields"
                     :sort-by.sync="flaggedSortBy" :sort-desc.sync="flaggedSortDesc"
                     no-local-sorting small responsive show-empty>
              <template slot="driver_name" slot-scope="data">
                <b>{{ data.item.driver_name }}</b>
              </template>
              <template slot="score" slot-scope="data">
                <span class="cs-list-num">{{ data.item.score == null ? '—' : data.item.score }}</span>
                <span class="cs-small text-muted"> {{ data.item.tier }}</span>
              </template>
              <template slot="severity_rank" slot-scope="data">
                <span v-for="f in data.item.flags" :key="f.key"
                      class="cs-flag-chip" :class="f.severity === 'critical' ? 'cs-flag-chip--critical' : 'cs-flag-chip--warning'"
                      :title="f.note">{{ f.label }}</span>
              </template>
              <template slot="miles" slot-scope="data">
                <span class="cs-list-num">{{ data.item.miles == null ? '—' : data.item.miles.toLocaleString() }}</span>
              </template>
              <template slot="days_since_coached_sort" slot-scope="data">
                <span v-if="data.item.last_coached" class="cs-list-num">
                  {{ data.item.days_since_coached }}d ago
                </span>
                <span v-else class="text-danger font-weight-bold">never</span>
              </template>
              <template slot="open_count" slot-scope="data">
                <span :class="data.item.open_count ? 'text-danger font-weight-bold' : 'text-muted'" class="cs-list-num">{{ data.item.open_count }}</span>
              </template>
              <template slot="actions" slot-scope="data">
                <b-button v-if="!data.item.queued" size="sm" variant="outline-primary"
                          @click.stop="openAssign(data.item.fd)">Assign</b-button>
                <b-badge v-else variant="light" title="Every flagged metric already has an open coaching ticket">queued</b-badge>
              </template>
            </b-table>
            <div v-if="flaggedRowsFiltered.length > perPage" class="cs-pager">
              <b-pagination v-model="flaggedPage" :total-rows="flaggedRowsFiltered.length"
                            :per-page="perPage" size="sm" class="mb-0" />
              <span class="cs-small text-muted">{{ flaggedRowsFiltered.length }} drivers</span>
            </div>
          </div>
        </div>
      </b-card>

      <p class="cs-small text-muted mt-3">
        A flag means two things at once: the driver's number is outside your fleet's own target
        <i>and</i> they rank near the bottom of the active roster on that metric this period —
        a pattern, not one bad clip. Assigning attaches the actual numbers to the ticket, so the
        reason it was raised survives the scorecard moving on.
      </p>
    </div>

    <!-- assign-coaching modal (mixin owns the flow) -->
    <b-modal id="cs-assign-modal" hide-footer :title="assignDriver ? 'Assign coaching — ' + assignDriver.driver.driver_name : ''">
      <div v-if="assignDriver">
        <div class="cs-label">Topic</div>
        <b-form-radio-group v-model="assignFlagKey" stacked class="mb-2">
          <b-form-radio v-for="f in assignDriver.flags" :key="f.key" :value="f.key" :disabled="isQueued(assignDriver.driver, f.label)">
            <span :class="f.severity === 'critical' ? 'text-danger' : 'text-warning'">{{ f.label }}</span>
            <span class="cs-small text-muted"> — {{ f.note }}</span>
            <b-badge v-if="isQueued(assignDriver.driver, f.label)" variant="light" class="ml-1">already queued</b-badge>
          </b-form-radio>
          <!-- not everything worth a conversation is a scorecard metric -->
          <b-form-radio :value="ASSIGN_CUSTOM">
            Something else
            <span class="cs-small text-muted"> — type your own topic</span>
          </b-form-radio>
        </b-form-radio-group>
        <b-form-input v-if="assignFlagKey === ASSIGN_CUSTOM" v-model="assignCustomTopic"
                      size="sm" class="mb-2 cs-custom-topic" placeholder="e.g. Radio etiquette" autofocus />

        <div class="cs-label">Why <span v-if="assignFlagKey !== ASSIGN_CUSTOM" class="cs-hint">pre-filled from the flag — edit if you have more context</span><span v-else class="cs-hint">optional — it goes on the record</span></div>
        <b-form-textarea v-model="assignWhy" rows="2" class="mb-2" />

        <div class="cs-label">Assign to</div>
        <b-form-select v-model="assignTo" :options="assignContactOptions" size="sm" class="mb-3 cs-assign-select" />

        <div class="cs-label">Due date</div>
        <b-form-input v-model="assignDueDate" type="date" class="mb-3 cs-date-input" />

        <div class="cs-small text-muted mb-3">
          <template v-if="assignFlagKey === ASSIGN_CUSTOM">
            The ticket is created against {{ assignDriver.driver.driver_name }} as a manual entry
            (no scorecard numbers attached), and appears in the queue as Open.
          </template>
          <template v-else>
            The ticket is created against {{ assignDriver.driver.driver_name }} with the {{ scorecardPeriodLabel }}
            numbers attached, and appears in the queue as Open.
          </template>
          <span v-if="assignTo && assignTo !== user.username">
            {{ assignedToLabel }} gets a notification that it landed on them.
          </span>
        </div>

        <div class="d-flex justify-content-end">
          <b-button variant="outline-secondary" class="mr-2" @click="$bvModal.hide('cs-assign-modal')">Cancel</b-button>
          <b-button variant="primary" :disabled="!canSaveAssign || assignSaving" @click="saveAssign">
            {{ assignSaving ? 'Assigning…' : 'Assign coaching' }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import coachingScopeMixin from '../mixins/coaching_scope_mixin'
import CoachingPortalHelp from './coaching_portal_help.vue'

export default {
  name: 'coaching_flagged',
  components: { CoachingPortalHelp },
  mixins: [coachingScopeMixin],
  data() {
    return {
      perPage: 15,
      periodPresets: [7, 30, 90],
      filtersExpanded: false,
      flaggedSearch: '',
      // FI-2110: set from ?driver_id= when arriving from the Individual Driver
      // page's critical-flag banner. an exact key match, not a search substring.
      driverFilter: '',
      flaggedSeverity: 'all',
      flaggedMetric: '',
      flaggedCoached: '',
      flaggedPage: 1,
      flaggedSortBy: 'severity_rank',
      flaggedSortDesc: true,
      severityFilterOptions: [
        { value: 'all', text: 'All severities' },
        { value: 'critical', text: 'Critical only' },
        { value: 'warning', text: 'Warning only' },
      ],
      // days_since_coached buckets; "never" is the biggest gap of all, so
      // the gap options include it
      coachedFilterOptions: [
        { value: '', text: 'Any' },
        { value: 'never', text: 'Never coached' },
        { value: 'gap30', text: 'No coaching in 30+ days' },
        { value: 'gap90', text: 'No coaching in 90+ days' },
        { value: 'recent30', text: 'Coached in last 30 days' },
      ],
    }
  },
  created() {
    this.hydrateDriverFromRoute()
  },
  computed: {
    // the id is the same `driver_id || driver_name` key coaching_scope_mixin builds
    // its rows with, so it matches r.key directly. the name is looked up from the
    // loaded rows rather than passed in the url — a driver name is personnel data and
    // has no business in browser history. a driver with no flags this period isn't in
    // flaggedRows, so the id stands in.
    driverFilterLabel() {
      if (!this.driverFilter) return ''
      const row = this.flaggedRows.find(r => String(r.key) === this.driverFilter)
      return (row && row.driver_name) || this.driverFilter
    },
    // only true when the driver genuinely has no flags this period. distinguishes
    // "clear this period" from "excluded by the other filters", which are different
    // things to tell someone.
    driverFilterHasFlags() {
      if (!this.driverFilter) return false
      return this.flaggedRows.some(r => String(r.key) === this.driverFilter)
    },
    flaggedRows() {
      const today = moment().startOf('day')
      return this.flaggedDrivers.map(fd => {
        const d = fd.driver
        const stat = this.ticketStatsByDriver[String(d.driver_id || '')]
          || this.ticketStatsByDriver[String(d.driver_name || '')] || {}
        const critical = fd.flags.filter(f => f.severity === 'critical').length
        const lastCoached = stat.lastCoached || null
        const days = lastCoached ? today.diff(moment(lastCoached.slice(0, 10)), 'days') : null
        return {
          key: fd.key,
          fd,
          driver_name: d.driver_name,
          score: d.compiled_score == null ? null : Math.round(d.compiled_score),
          tier: d.compiled_score_tier || '',
          miles: d.miles_driven == null ? null : Math.round(d.miles_driven),
          flags: fd.flags,
          // criticals dominate the sort; count breaks ties
          severity_rank: critical * 100 + fd.flags.length,
          critical_count: critical,
          last_coached: lastCoached,
          days_since_coached: days,
          // "never coached" sorts as oldest — it is the biggest gap
          days_since_coached_sort: days == null ? 100000 : days,
          open_count: stat.openCoaching || 0,
          queued: this.allFlagsQueued(fd),
        }
      })
    },
    // every flagged metric present this period, for the Metric dropdown
    metricFilterOptions() {
      const labels = new Set()
      this.flaggedRows.forEach(r => r.flags.forEach(f => labels.add(f.label)))
      return [
        { value: '', text: 'All metrics' },
        ...[...labels].sort().map(l => ({ value: l, text: l })),
      ]
    },
    // active filter dimensions for the mobile toggle badge (search excluded)
    activeFilterCount() {
      let n = 0
      if (this.flaggedSeverity !== 'all') n++
      if (this.flaggedMetric) n++
      if (this.flaggedCoached) n++
      if (this.scorecardFrom || this.scorecardTo) n++
      return n
    },
    assignedToLabel() {
      const opt = this.assignContactOptions.find(o => o.value === this.assignTo)
      return opt ? opt.text : 'They'
    },
    filtersDirty() {
      return this.driverFilter !== '' || this.otherFiltersDirty
    },
    // the driver chip clears itself, so "Reset filters" is only offered for the
    // filters the chip does not cover — otherwise a deep link shows two buttons that
    // both look like they undo the same thing.
    otherFiltersDirty() {
      return this.flaggedSearch !== '' || this.flaggedSeverity !== 'all'
        || this.flaggedMetric !== '' || this.flaggedCoached !== ''
        || this.scorecardFrom !== '' || this.scorecardTo !== ''
    },
    flaggedRowsFiltered() {
      const search = this.flaggedSearch.trim().toLowerCase()
      return this.flaggedRows.filter(r => {
        if (this.driverFilter && String(r.key) !== this.driverFilter) return false
        if (this.flaggedSeverity === 'critical' && !r.critical_count) return false
        if (this.flaggedSeverity === 'warning' && r.critical_count === r.flags.length) return false
        if (this.flaggedMetric && !r.flags.some(f => f.label === this.flaggedMetric)) return false
        if (this.flaggedCoached) {
          const days = r.days_since_coached // null = never coached
          if (this.flaggedCoached === 'never' && days != null) return false
          if (this.flaggedCoached === 'gap30' && days != null && days < 30) return false
          if (this.flaggedCoached === 'gap90' && days != null && days < 90) return false
          if (this.flaggedCoached === 'recent30' && (days == null || days >= 30)) return false
        }
        if (!search) return true
        return r.driver_name.toLowerCase().includes(search)
          || r.flags.some(f => f.label.toLowerCase().includes(search))
      })
    },
    flaggedRowsPage() {
      const sorted = this.sortRows(this.flaggedRowsFiltered, this.flaggedSortBy, this.flaggedSortDesc)
      const start = (this.flaggedPage - 1) * this.perPage
      return sorted.slice(start, start + this.perPage)
    },
    flaggedFields() {
      return [
        { key: 'driver_name', label: 'Driver', sortable: true, thClass: 'cs-driver-cell', tdClass: 'cs-driver-cell' },
        { key: 'score', label: 'Score', sortable: true, thStyle: { width: '110px' } },
        { key: 'severity_rank', label: 'Flags', sortable: true },
        { key: 'miles', label: 'Miles', sortable: true, thStyle: { width: '86px' } },
        { key: 'days_since_coached_sort', label: 'Last coached', sortable: true, thStyle: { width: '116px' } },
        { key: 'open_count', label: 'Open', sortable: true, thStyle: { width: '68px' } },
        { key: 'actions', label: '', thStyle: { width: '104px' } },
      ]
    },
  },
  watch: {
    // back/forward has to move the filter with the url, or the two disagree after a
    // clear. same contract as driver_manager_individual.vue's driver_id watcher.
    '$route.query.driver_id'() {
      this.hydrateDriverFromRoute()
    },
    // filters reset pagination so a narrowed set never shows an empty page
    flaggedSearch() { this.flaggedPage = 1 },
    flaggedSeverity() { this.flaggedPage = 1 },
    flaggedMetric() { this.flaggedPage = 1 },
    flaggedCoached() { this.flaggedPage = 1 },
  },
  methods: {
    // a preset is "active" when the month pickers hold exactly its range —
    // no separate state to fall out of sync when a month is edited by hand
    periodPresetActive(days) {
      return this.scorecardFrom === moment().subtract(days, 'days').format('YYYY-MM')
        && this.scorecardTo === moment().format('YYYY-MM')
    },
    setPeriodPreset(days) {
      if (this.periodPresetActive(days)) {
        this.scorecardFrom = ''
        this.scorecardTo = ''
      } else {
        this.scorecardFrom = moment().subtract(days, 'days').format('YYYY-MM')
        this.scorecardTo = moment().format('YYYY-MM')
      }
      this.onPeriodChange()
    },
    // the scorecard window changed: re-query (the mixin swaps a backwards
    // pair rather than erroring)
    onPeriodChange() {
      this.flaggedPage = 1
      this.loadFlaggedDrivers()
    },
    hydrateDriverFromRoute() {
      const id = this.$route.query.driver_id
      const next = id ? String(id) : ''
      if (next !== this.driverFilter) {
        this.driverFilter = next
        this.flaggedPage = 1
      }
    },
    clearDriverFilter() {
      // nothing to clear: skip the navigation entirely rather than firing a
      // duplicate replace whose rejection we would then have to swallow
      if (!this.driverFilter) return
      this.driverFilter = ''
      this.flaggedPage = 1
      // drop the param so a refresh or back/forward doesn't reapply the filter
      const query = { ...this.$route.query }
      delete query.driver_id
      this.$router.replace({ query }).catch(() => {})
    },
    clearFilters() {
      this.clearDriverFilter()
      this.flaggedSearch = ''
      this.flaggedSeverity = 'all'
      this.flaggedMetric = ''
      this.flaggedCoached = ''
      this.flaggedPage = 1
      const hadPeriod = this.scorecardFrom || this.scorecardTo
      this.scorecardFrom = ''
      this.scorecardTo = ''
      if (hadPeriod) this.loadFlaggedDrivers()
    },
    onTenantScopeReset() {
      // the mixin already reset its own scorecardFrom/To
      this.clearDriverFilter()
      this.flaggedSearch = ''
      this.flaggedSeverity = 'all'
      this.flaggedMetric = ''
      this.flaggedCoached = ''
      this.flaggedPage = 1
    },
  },
}
</script>

<style scoped>
/* the app shell centers text (#app) — this page is left-aligned, so inline
   controls (the mobile Filters toggle) sit left like everything else */
.cs-root { padding-bottom: 24px; text-align: left; }
.cs-stale { opacity: 0.6; }
.cs-small { font-size: 0.85em; }
.cs-hint { font-weight: 400; font-size: 0.85em; opacity: 0.7; }
.cs-tenant-note { margin-top: 10px; font-size: 0.85em; color: var(--fi-text-muted, #6b7280); }

.cs-panel-head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; font-weight: 600; font-size: 0.95em;
  border-bottom: 1px solid var(--fi-line, #dee2e6);
}
.cs-panel-body { padding: 8px 14px; }

/* search/filter bar: the Coaching Tickets cp-filter-row recipe, cs-scoped */
.cs-filter-row {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 8px 12px;
  margin-top: 14px; padding-bottom: 8px;
  border-bottom: 1px solid #eef0f3;
  font-size: 13px;
}
.cs-filter-field { display: inline-flex; align-items: center; gap: 4px; }
.cs-filter-lbl {
  font-size: 10.5px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .4px;
  color: #6b7280; white-space: nowrap;
}
.cs-filter-select { width: 180px; font-size: 13px; }
.cs-filter-select--narrow { width: 150px; }
.cs-filter-month { width: 150px; font-size: 13px; }
/* empty month input: placeholder-strength ink (safari renders the current
   month as ghost content, which reads as an active filter) */
.cs-filter-month.cs-month-ghost { color: #9aa4b1; }
.cs-filter-month.cs-month-ghost:focus { color: inherit; }
/* 7d/30d/90d one-tap presets (od-chip recipe, compact) */
.cs-preset-chip {
  background: #f8fafc; border: 1px solid #d6dde6; border-radius: 999px;
  padding: 3px 10px; font-size: 12px; font-weight: 600; color: #4b5563;
  cursor: pointer; transition: background .12s, border-color .12s, color .12s;
}
.cs-preset-chip + .cs-preset-chip { margin-left: 4px; }
.cs-preset-chip:hover { background: #eef4fb; border-color: #b8c8da; }
.cs-preset-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
.cs-search {
  margin-left: auto;
  position: relative;
  display: inline-flex; align-items: center;
  flex: 0 1 220px; min-width: 160px;
}
.cs-search-icon {
  position: absolute; left: 11px;
  color: #9aa3ad; font-size: 12px; pointer-events: none;
}
.cs-search-input {
  width: 100%;
  padding: 5px 26px 5px 28px;
  font-size: 13px;
  border: 1px solid var(--fi-line, #d6dde6); border-radius: 999px;
  background: var(--fi-surface, #fff); color: var(--fi-text, #1d1d1f);
  transition: border-color .12s, box-shadow .12s;
}
.cs-search-input::placeholder { color: #9aa3ad; }
.cs-search-input:focus {
  outline: none;
  border-color: var(--fi-ops-accent, #3a6e99);
  box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.2);
}
.cs-search-clear {
  position: absolute; right: 6px;
  background: none; border: none; padding: 2px 5px;
  color: #9aa3ad; cursor: pointer; font-size: 12px; line-height: 1;
}
.cs-search-clear:hover { color: #4b5563; }
.cs-results-row {
  display: flex; align-items: center; gap: 10px;
  margin-top: 6px;
}
.cs-results-count { font-size: 12px; color: #6b7280; font-variant-numeric: tabular-nums; }
.cs-clear-filters {
  background: none; border: none; padding: 0;
  font-size: 12px; font-weight: 600; color: var(--fi-ops-accent-strong, #2d5a80);
  cursor: pointer;
}
.cs-clear-filters:hover { text-decoration: underline; }
.cs-pager {
  display: flex; align-items: center; gap: 10px;
  padding-top: 8px; margin-top: 2px;
  border-top: 1px solid var(--fi-line, #eee);
}
.cs-list-num { font-variant-numeric: tabular-nums; }
.cs-flag-chip {
  display: inline-block; margin: 1px 4px 1px 0;
  padding: 1px 7px;
  font-size: 11px; line-height: 1.5; font-weight: 600;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.cs-flag-chip--critical { background: #fbe3e5; color: #b91c1c; border-color: #f5c0c4; }
.cs-flag-chip--warning  { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.cs-label {
  font-size: 0.8em; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; margin-bottom: 5px;
}
.cs-date-input { max-width: 220px; }
.cs-assign-select { max-width: 260px; }
/* indents under its "Something else" radio (bootstrap's 1.25rem custom-control pad) */
.cs-custom-topic { max-width: 320px; margin-left: 1.25rem; }

.cs-table-inline >>> .table-responsive { overflow-x: auto; overflow-y: hidden; }
.cs-table-inline >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.cs-table-inline >>> .table.b-table th,
.cs-table-inline >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.cs-table-inline >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.cs-table-inline >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }
.cs-table-inline >>> .cs-driver-cell { white-space: nowrap; width: 1%; }
.cs-table-inline >>> th[aria-sort] { background-image: none !important; white-space: nowrap; }
.cs-table-inline >>> th[aria-sort]::before { display: none !important; }
.cs-table-inline >>> th[aria-sort]::after {
  display: inline-block !important; margin-left: 4px; font-size: 10px; color: #999;
  vertical-align: middle; float: none !important; width: auto !important;
  height: auto !important; opacity: 1 !important;
}
.cs-table-inline >>> th[aria-sort="none"]::after { content: "\2195" !important; }
.cs-table-inline >>> th[aria-sort="ascending"]::after { content: "\25B2" !important; color: var(--fi-rail-accent); }
.cs-table-inline >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }

/* mobile filters toggle (the ERM Event Log pattern): hidden above 600px */
.cs-mobile-only { display: none; }
.cs-filter-toggle {
  align-items: center; gap: 7px;
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6); border-radius: 999px;
  padding: 7px 14px; margin: 14px 0 0;
  font-size: 13.5px; font-weight: 600; color: var(--fi-text, #374151);
  cursor: pointer; transition: background .12s, border-color .12s;
}
.cs-filter-toggle:focus-visible { outline: 2px solid var(--fi-ops-accent, #3a6e99); outline-offset: 2px; }
.cs-filter-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  font-size: 11px; font-weight: 700; line-height: 1;
}
@media (max-width: 600px) {
  /* full-width search leading the filter row; 16px to dodge iOS focus-zoom */
  .cs-search { margin-left: 0; flex: 1 0 100%; order: -1; }
  .cs-search-input { font-size: 16px; }
  .cs-filter-field { flex: 1 1 auto; }
  .cs-filter-month, .cs-filter-select { width: 100%; font-size: 16px; }
  /* mobile-only affordances turn on here */
  .cs-mobile-only { display: inline-flex; }
  /* collapsed: every filter control hides except the search pill, so the
     phone opens straight to rows. the toggle above flips it open. */
  .cs-filters-collapsed .cs-filter-field { display: none; }
}

/* mobile: the header stays one row — title and actions inline. overrides
   FiPageRail's own 600px rule that wraps actions onto a full-width second
   row (fine for pages with a toolbar; a waste for one info icon). */
@media (max-width: 600px) {
  .cs-rail.fi-pr-rail { flex-wrap: nowrap; row-gap: 0; }
  .cs-rail >>> .fi-pr-actions { width: auto; }
}
</style>
